// components/AwsPricingCatalog.jsx — tab Katalog Harga.
//
// Penjelajah mentah atas aws_price_product × aws_price_term. Gunanya bukan
// menghitung, tapi MEMBUKTIKAN: ketika kalkulator memberi angka yang mencurigakan,
// di sini bisa dilihat baris katalog yang menghasilkannya.
//
// Kolom pre_installed_sw ditampilkan secara mencolok karena itulah pembeda yang
// membuat (region, instance_type, os, tenancy) tidak unik — m6i.2xlarge/Linux/
// Shared di ap-southeast-3 punya empat harga on-demand, dan tiga di antaranya
// adalah SQL Server Web/Standard/Enterprise.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { Search, Loader2, AlertTriangle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const REGIONS = ['ap-southeast-3', 'ap-southeast-1'];
const PAGE = 50;

export default function AwsPricingCatalog() {
  const { t } = useLang();
  const [f, setF] = useState({
    region: 'ap-southeast-3', service_code: '', instance_type: '', os: '',
    tenancy: '', pre_installed_sw: '', term_type: '', offering_class: '',
    lease_length: '', purchase_option: '', q: '',
  });
  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/aws/pricing/facets', { params: { region: f.region } })
      .then(r => setFacets(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, [f.region]);

  const load = useCallback(async (off) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE, offset: off };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/api/aws/pricing/catalog', { params });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(0); }, [load]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const clear = () => setF({
    region: f.region, service_code: '', instance_type: '', os: '', tenancy: '',
    pre_installed_sw: '', term_type: '', offering_class: '', lease_length: '',
    purchase_option: '', q: '',
  });

  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Sel label={t('awsp.region')} value={f.region} onChange={v => set('region', v)}>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </Sel>
          <SSel label={t('awsp.service')} value={f.service_code} onChange={v => set('service_code', v)}
            options={facets?.service_code} allLabel={t('awsp.all')} />
          <SSel label={t('awsp.instanceType')} value={f.instance_type} onChange={v => set('instance_type', v)}
            options={facets?.instance_type} allLabel={t('awsp.all')} />
          <SSel label={t('awsp.os')} value={f.os} onChange={v => set('os', v)}
            options={facets?.os} allLabel={t('awsp.all')} />
          <SSel label={t('awsp.tenancy')} value={f.tenancy} onChange={v => set('tenancy', v)}
            options={facets?.tenancy} allLabel={t('awsp.all')} />
          <SSel label={t('awsp.preInstalledSw')} value={f.pre_installed_sw} onChange={v => set('pre_installed_sw', v)}
            options={facets?.pre_installed_sw} allLabel={t('awsp.all')} />
          <Sel label={t('awsp.termType')} value={f.term_type} onChange={v => set('term_type', v)}>
            <option value="">{t('awsp.all')}</option>
            <option value="ondemand">On-Demand</option>
            <option value="ri">Reserved Instance</option>
            <option value="sp">Savings Plan</option>
          </Sel>
          <Sel label={t('awsp.offeringClass')} value={f.offering_class} onChange={v => set('offering_class', v)}>
            <option value="">{t('awsp.all')}</option>
            <option value="standard">standard</option>
            <option value="convertible">convertible</option>
          </Sel>
          <Sel label={t('awsp.lease')} value={f.lease_length} onChange={v => set('lease_length', v)}>
            <option value="">{t('awsp.all')}</option>
            <option value="1yr">1yr</option>
            <option value="3yr">3yr</option>
          </Sel>
          <Sel label={t('awsp.payment')} value={f.purchase_option} onChange={v => set('purchase_option', v)}>
            <option value="">{t('awsp.all')}</option>
            <option value="No Upfront">No Upfront</option>
            <option value="Partial Upfront">Partial Upfront</option>
            <option value="All Upfront">All Upfront</option>
          </Sel>
          <label className="block col-span-2">
            <span className="block text-xs font-medium text-gray-600 mb-1">{t('awsp.search')}</span>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
              <input
                value={f.q}
                onChange={e => set('q', e.target.value)}
                placeholder={t('awsp.catalog.searchHint')}
                className="w-full pl-8 pr-2 py-1.5 rounded border border-gray-300 text-sm"
              />
            </div>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clear}
            className="px-3 py-1.5 rounded border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> {t('awsp.clearFilters')}
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            {t('awsp.catalog.rowCount').replace('{n}', total.toLocaleString('en-US'))}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                {['instanceType', 'os', 'tenancy', 'preInstalledSw', 'termType', 'offeringClass',
                  'lease', 'payment', 'range', 'upfront', 'rate', 'unit'].map(k => (
                  <th key={k} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">
                    {t(`awsp.${k}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="px-2 py-6 text-center text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{t('awsp.loading')}
                </td></tr>
              )}
              {!loading && !rows.length && (
                <tr><td colSpan={12} className="px-2 py-6 text-center text-gray-500">{t('awsp.catalog.empty')}</td></tr>
              )}
              {!loading && rows.map(r => (
                <tr key={`${r.sku}-${r.term_id}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.instance_type || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.os || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.tenancy || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.pre_installed_sw || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.term_type}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.offering_class || r.plan_type || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.lease_length || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.purchase_option || '—'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">
                    {r.begin_range}–{r.end_range === null ? '∞' : r.end_range}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {Number(r.upfront_usd) > 0 ? `$${Number(r.upfront_usd).toLocaleString('en-US')}` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{Number(r.rate_usd)}</td>
                  <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{r.unit || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600">
          <span>{t('awsp.catalog.page').replace('{p}', String(page)).replace('{t}', String(pages))}</span>
          <div className="flex gap-1">
            <button
              onClick={() => load(Math.max(0, offset - PAGE))}
              disabled={offset === 0 || loading}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => load(offset + PAGE)}
              disabled={offset + PAGE >= total || loading}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white">
        {children}
      </select>
    </label>
  );
}

// Filter dengan daftar panjang (instance_type bisa 400+ nilai) memakai
// pencarian, bukan <select> yang harus di-scroll. `options` di sini adalah
// hasil facet dari backend: [{value, count}].
function SSel({ label, value, onChange, options, allLabel }) {
  const { t } = useLang();
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={(options || []).map(o => ({ value: o.value, label: o.value, count: o.count }))}
        allLabel={allLabel}
        placeholder={t('awsp.searchPlaceholder')}
      />
    </label>
  );
}
